/**
 * Route to Navigation Converter
 *
 * Transforms RouteExtension objects from the route registry into NavItemConfig objects
 * for use in NavigationSide component. Handles hierarchy, permissions, and icon resolution.
 */
import React from 'react';
import { RouteExtension } from '../types/route-config';
import { resolveIconComponent } from './icon-resolver';
import { getRouteRegistry } from '../registration/route-registry';

/**
 * Plugin metadata for navigation grouping
 */
export interface PluginNavMetadata {
  /**
   * Display name for the plugin. When the plugin shares a section with other
   * plugins this is the caption above its own items inside the secondary
   * panel; when it is a section of its own it is the rail label.
   */
  displayName: string;
  /** Order of the plugin's items within its section (lower = higher) */
  order: number;
  /**
   * Icon name shown on the rail when the plugin is a section of its own, and
   * beside its group header in the secondary panel when it shares one.
   */
  icon?: string;
  /**
   * Section this plugin's nav items belong to. Several plugins share one
   * section — one rail entry — which is what stops the rail growing an icon
   * per plugin. Defaults to the plugin id, i.e. a section of its own, so an
   * unmapped or newly added plugin still gets a rail entry.
   */
  section?: string;
  /**
   * Always show the plugin caption: even when it is the only plugin in its
   * section, and even when it restates its only item.
   */
  showCaption?: boolean;
}

/**
 * A navigation section: one rail entry, holding the items of one or more
 * plugins plus any app-level extras slotted into it.
 */
export interface NavSectionMetadata {
  /** Rail label for the section */
  displayName: string;
  /** Order of the section in the rail (lower = higher) */
  order: number;
  /** Icon name shown on the rail */
  icon?: string;
  /** One-line summary shown under the section title in the secondary panel */
  description?: string;
  /**
   * The section is a single page: its one item goes on the rail as a plain
   * link, with no header and no secondary panel to open first. Anything
   * beyond the first item is dropped, so keep it to one.
   */
  railLink?: boolean;
  /**
   * Caption heading the section's items in the secondary panel when the
   * section holds a single plugin (whose own caption would be dropped), so
   * the items — app-level extras included — read as one named group.
   */
  caption?: string;
}

/**
 * Navigation item configuration (matches design system)
 */
export interface NavItemConfig {
  id: string;
  label: string;
  path: string;
  icon: React.ReactElement<{ color?: string; size?: number }>;
  onClick?: () => void;
  children?: NavItemConfig[];
  depth?: number;
  /** Plugin ID this nav item belongs to */
  pluginId?: string;
  /** Whether this is a group separator */
  isSeparator?: boolean;
  /** Whether this is a group header (section title) */
  isGroupHeader?: boolean;
  /** Summary shown under a group header's title in the secondary panel. */
  description?: string;
  /**
   * Whether this is a caption inside the secondary panel, labelling the block
   * of items belonging to one plugin within a shared section. Non-interactive.
   */
  isCaption?: boolean;
  /**
   * Opens its own block in the panel: this item stands for a plugin whose
   * caption was dropped because the caption said nothing the item did not.
   * Without the gap the bare item reads as the last entry of the block above
   * it (QA ledger TRK-004).
   */
  startsBlock?: boolean;
  /**
   * Placement of an app-level extra within its section, compared against the
   * `order` of the section's plugins. Unused for plugin-owned routes, which
   * order by their own route metadata.
   */
  order?: number;
  /** Treats `path` as an external URL — opens in a new tab, bypassing the router. */
  external?: boolean;
  /** Optional node rendered after the label (e.g. a small badge/pill). */
  badge?: React.ReactNode;
  /**
   * Extra paths that mark this item as current, for detail routes that are
   * siblings of the nav path rather than nested under it — e.g. a shipment
   * detail at `/shipments/:id` belongs to the `/shipments/all` entry.
   */
  matchPaths?: string[];
}

/**
 * Options for building navigation from routes
 */
export interface BuildNavOptions {
  /** Routes to process (defaults to root routes from registry) */
  routes?: RouteExtension[];

  /** Current path for active state determination */
  currentPath?: string;

  /** User permissions for filtering */
  userPermissions?: string[];

  /** Audience of the current user: 'partner' (external) or 'internal'. */
  userAudience?: 'internal' | 'partner';

  /** Custom filter function */
  filterFn?: (route: RouteExtension) => boolean;

  /** Navigation click handler */
  onNavigate?: (path: string) => void;

  /** Current depth (for recursion, default 0) */
  depth?: number;

  /** Whether to group nav items by plugin with separators (default: false) */
  groupByPlugin?: boolean;

  /** Plugin metadata for grouping (pluginId -> metadata) */
  pluginMetadata?: Record<string, PluginNavMetadata>;

  /** Section metadata for grouping (sectionId -> metadata) */
  navSections?: Record<string, NavSectionMetadata>;

  /**
   * App-level nav items to slot into a section (sectionId -> items), for
   * entries the app owns rather than a plugin: the workspace home, an
   * entitled external tool. Passing them here rather than splicing them into
   * the built list by index means they are placed by section and `order`, and
   * a section made only of extras still gets its rail entry.
   */
  sectionExtras?: Record<string, NavItemConfig[]>;
}

/**
 * Check if user has required permissions for a route
 */
function hasPermission(
  route: RouteExtension,
  userPermissions?: string[]
): boolean {
  if (!route.permissions || route.permissions.length === 0) {
    return true; // No permissions required
  }

  if (!userPermissions || userPermissions.length === 0) {
    return false; // Route requires permissions but user has none
  }

  // Check if user has any of the required permissions. A user permission grants
  // a required one if it is the global wildcard '*', an exact match, or a
  // segment-wildcard match (e.g. 'vendor:*' grants 'vendor:application:read') —
  // mirroring the backend RBAC wildcard semantics.
  return route.permissions.some((permission) => {
    const permissionPattern = typeof permission === 'string' ? permission : permission.pattern;
    return userPermissions.some((userPerm) => permissionMatches(userPerm, permissionPattern));
  });
}

/**
 * Whether a route's nav item should be shown to the current user's audience.
 * Routes default to 'all'; 'partner'-only items are hidden from internal users
 * and 'internal'-only items from external partner users.
 */
function audienceMatches(
  route: RouteExtension,
  userAudience?: 'internal' | 'partner'
): boolean {
  const audience = route.metadata?.audience ?? 'all';
  if (audience === 'all' || !userAudience) return true;
  return audience === userAudience;
}

/**
 * Whether a user-held permission grants a required permission.
 *
 * Mirrors the backend PermissionResolver (and plugin-sdk `matchesPermission`)
 * semantics: `*` grants everything, exact match, and PREFIX wildcards
 * (`order:*` grants `order:sales:read`; `order:sales:*` grants `order:sales:read`).
 *
 * Kept in sync manually with `@lognai/plugin-sdk` `matchesPermission` — frontend-core
 * cannot depend on plugin-sdk. Using prefix semantics here (rather than the old
 * equal-segment-count rule) is what makes the sidebar agree with the route guard and
 * the backend: a domain-wildcard grant such as `order:*` (e.g. a FULL-access partner)
 * now correctly reveals `order:sales:read`-gated nav items instead of hiding them.
 */
function permissionMatches(userPerm: string, required: string): boolean {
  if (userPerm === '*' || userPerm === required) {
    return true;
  }
  // Prefix wildcard: 'order:*' -> prefix 'order:' must lead `required`.
  if (userPerm.endsWith(':*')) {
    const prefix = userPerm.slice(0, -1); // drop the '*', keep the trailing ':'
    return required.startsWith(prefix);
  }
  return false;
}

/**
 * Check if route should be shown in navigation
 */
function shouldShowInNav(route: RouteExtension): boolean {
  // Must have showInNav set to true
  if (!route.metadata?.showInNav) {
    return false;
  }

  // Must have a name
  if (!route.name) {
    return false;
  }

  // Skip routes with dynamic parameters (e.g., /:id/)
  // These are typically detail pages, not nav items
  if (route.path.includes(':')) {
    return false;
  }

  return true;
}

/**
 * Get sort order for a route
 * Uses metadata.order, falling back to route.order, falling back to 999
 */
function getSortOrder(route: RouteExtension): number {
  if (route.metadata?.order !== undefined) {
    return route.metadata.order;
  }
  if (route.order !== undefined) {
    return route.order;
  }
  return 999; // Default to end
}

/**
 * Convert a single RouteExtension to NavItemConfig
 */
function routeToNavItem(
  route: RouteExtension,
  options: BuildNavOptions
): NavItemConfig {
  const { onNavigate, depth = 0 } = options;
  const registry = getRouteRegistry();

  // Resolve icon
  const icon = resolveIconComponent(route.metadata?.icon, 20);

  // Get children for this route (don't group children by plugin)
  const childRoutes = registry.getChildRoutes(route.id);
  const children = childRoutes.length > 0
    ? buildNavigationFromRoutes({
        ...options,
        routes: childRoutes,
        depth: depth + 1,
        groupByPlugin: false,
      })
    : undefined;

  return {
    id: route.id,
    label: route.name || route.path,
    path: route.path,
    icon,
    onClick: () => {
      if (onNavigate) {
        onNavigate(route.path);
      }
    },
    children,
    depth,
    pluginId: route.pluginId,
    ...(route.metadata?.matchPaths ? { matchPaths: route.metadata.matchPaths as string[] } : {}),
    // A plugin's live badge (plugin-sdk `nav.badge`), drawn after the label.
    ...(route.metadata?.navBadge ? { badge: React.createElement(route.metadata.navBadge) } : {}),
  };
}

/**
 * Build navigation items from routes
 *
 * @param options - Build options
 * @returns Array of NavItemConfig for NavigationSide
 */
export function buildNavigationFromRoutes(
  options: BuildNavOptions = {}
): NavItemConfig[] {
  const {
    routes: inputRoutes,
    userPermissions,
    userAudience,
    filterFn,
    depth = 0,
    groupByPlugin = false,
    pluginMetadata = {},
    navSections = {},
    sectionExtras = {},
  } = options;

  const registry = getRouteRegistry();

  // Get routes to process (defaults to root routes)
  const routes = inputRoutes || registry.getRootRoutes();

  // Filter routes
  const filteredRoutes = routes
    .filter(shouldShowInNav)
    .filter((route) => hasPermission(route, userPermissions))
    .filter((route) => audienceMatches(route, userAudience))
    .filter((route) => !filterFn || filterFn(route));

  // If grouping is on, organize routes into sections and add separators
  if (groupByPlugin && depth === 0) {
    // Routes group by *section*, not by plugin: several plugins can share one
    // section, and a section is one rail entry. That is what stops the rail
    // growing an icon per plugin — Invoices, Landed Cost and Freight Audit are
    // three plugins behind a single "Finance" entry. A plugin with no `section`
    // is a section of its own, so an unmapped or newly added plugin still
    // appears rather than silently vanishing.
    // A route may name its own section (a plugin whose pages belong to several
    // sections, such as one nav entry per published form); the plugin's mapped
    // section is the default.
    const sectionKeyOf = (route: RouteExtension): string =>
      (typeof route.metadata?.section === 'string' && route.metadata.section) ||
      pluginMetadata[route.pluginId]?.section ||
      route.pluginId;

    const routesBySection = new Map<string, Map<string, RouteExtension[]>>();
    for (const route of filteredRoutes) {
      const sectionKey = sectionKeyOf(route);
      let byPlugin = routesBySection.get(sectionKey);
      if (!byPlugin) {
        byPlugin = new Map<string, RouteExtension[]>();
        routesBySection.set(sectionKey, byPlugin);
      }
      const pluginRoutes = byPlugin.get(route.pluginId) ?? [];
      pluginRoutes.push(route);
      byPlugin.set(route.pluginId, pluginRoutes);
    }

    // A section can be made entirely of app-level extras (the workspace home
    // and Drafts own no plugin routes), so it needs a rail entry even though
    // no route landed in it.
    for (const [sectionKey, extras] of Object.entries(sectionExtras)) {
      if (extras.length > 0 && !routesBySection.has(sectionKey)) {
        routesBySection.set(sectionKey, new Map<string, RouteExtension[]>());
      }
    }

    // A section-of-one falls back to its plugin's own order, which is what
    // keeps an unmapped plugin sitting where it always sat.
    const sectionOrder = (key: string): number =>
      navSections[key]?.order ?? pluginMetadata[key]?.order ?? 50;

    const sortedSectionKeys = Array.from(routesBySection.keys()).sort(
      (a, b) => sectionOrder(a) - sectionOrder(b)
    );

    const navItems: NavItemConfig[] = [];

    sortedSectionKeys.forEach((sectionKey, sectionIndex) => {
      const byPlugin = routesBySection.get(sectionKey)!;
      const extras = sectionExtras[sectionKey] ?? [];

      // One block per plugin, one per extra item, interleaved by order.
      // Blocks rather than a single flat sort: a caption has to stay attached
      // to the items it labels, which a flat sort would separate.
      interface SectionBlock {
        key: string;
        order: number;
        caption?: string;
        items: NavItemConfig[];
      }
      const blocks: SectionBlock[] = [];

      for (const [pluginId, pluginRoutes] of byPlugin) {
        const ordered = [...pluginRoutes].sort((a, b) => getSortOrder(a) - getSortOrder(b));
        blocks.push({
          key: pluginId,
          order: pluginMetadata[pluginId]?.order ?? 50,
          caption: pluginMetadata[pluginId]?.displayName ?? pluginId,
          items: ordered.map((route) => routeToNavItem(route, { ...options, depth })),
        });
      }
      for (const extra of extras) {
        blocks.push({ key: extra.id, order: extra.order ?? 50, items: [extra] });
      }
      blocks.sort((a, b) => a.order - b.order);

      // A one-page section links straight from the rail. Its item carries its
      // own label and icon, so it needs no header — and no separator, which
      // the rail only uses to end a section.
      if (navSections[sectionKey]?.railLink) {
        const link = blocks[0]?.items[0];
        if (link) navItems.push(link);
        return;
      }

      const sectionLabel =
        navSections[sectionKey]?.displayName ??
        pluginMetadata[sectionKey]?.displayName ??
        sectionKey;
      const sectionIcon = navSections[sectionKey]?.icon ?? pluginMetadata[sectionKey]?.icon;

      // 1. Section header — the rail entry
      navItems.push({
        id: `header-${sectionKey}`,
        label: sectionLabel,
        path: '',
        icon: sectionIcon
          ? resolveIconComponent({ name: sectionIcon, size: 20 }, 20)
          : (null as any),
        isGroupHeader: true,
        description: navSections[sectionKey]?.description,
        pluginId: sectionKey,
      });

      // 2. The section's blocks. Captions only earn their place when the panel
      // actually mixes plugins; with a single plugin the section header already
      // says everything a caption would.
      const useCaptions = byPlugin.size > 1;

      const sectionCaption = navSections[sectionKey]?.caption;
      if (sectionCaption && !useCaptions) {
        navItems.push({
          id: `caption-${sectionKey}`,
          label: sectionCaption,
          path: '',
          icon: sectionIcon
            ? resolveIconComponent({ name: sectionIcon, size: 16 }, 16)
            : (null as any),
          isCaption: true,
          pluginId: sectionKey,
        });
      }

      blocks.forEach((block, blockIndex) => {
        const forceCaption = pluginMetadata[block.key]?.showCaption === true;
        const captionEnabled = useCaptions || forceCaption;
        /*
          A caption restating its only item — "Tracking" above "Tracking" — is
          noise, and it is worse than noise: the caption is not clickable, so
          the panel shows the same word twice and only one of them works
          (QA ledger TRK-004).

          Dropping it used to be limited to the leading block, because lower
          down the bare item reads as the last entry of the block above it:
          "Landed Cost" sitting under "INVOICES" looks like an invoices page.
          That is a spacing problem, not a reason to keep the duplicate — so
          the item now carries `startsBlock`, which buys it the same gap the
          caption would have had.
        */
        const restatesItem =
          !forceCaption &&
          block.items.length === 1 &&
          !!block.caption &&
          block.items[0]!.label.trim().toLowerCase() === block.caption.trim().toLowerCase();

        if (captionEnabled && restatesItem && blockIndex > 0) {
          block.items[0] = { ...block.items[0]!, startsBlock: true };
        }

        if (captionEnabled && block.caption && !restatesItem) {
          navItems.push({
            id: `caption-${sectionKey}-${block.key}`,
            label: block.caption,
            path: '',
            icon: pluginMetadata[block.key]?.icon
              ? resolveIconComponent({ name: pluginMetadata[block.key]!.icon!, size: 16 }, 16)
              : (null as any),
            isCaption: true,
            pluginId: sectionKey,
          });
        }
        navItems.push(...block.items);
      });

      // 3. Separator after the section (except the last)
      if (sectionIndex < sortedSectionKeys.length - 1) {
        navItems.push({
          id: `separator-${sectionKey}`,
          label: '',
          path: '',
          icon: null as any,
          isSeparator: true,
          pluginId: sectionKey,
        });
      }
    });

    return navItems;
  }

  // Standard flow (no grouping): sort and convert
  const navItems = filteredRoutes
    .sort((a, b) => getSortOrder(a) - getSortOrder(b))
    .map((route) => routeToNavItem(route, { ...options, depth }));

  return navItems;
}

/**
 * Find the active route ID from current path
 *
 * @param path - Current path (e.g., "/warehouse/list")
 * @returns Route ID or undefined
 */
export function getActiveRouteId(path: string): string | undefined {
  const registry = getRouteRegistry();
  const route = registry.getRouteByPath(path);
  return route?.id;
}

/**
 * Check if a route or any of its descendants matches the given path
 *
 * @param route - Route to check
 * @param path - Path to match
 * @returns True if route or descendant matches
 */
export function isRouteOrDescendantActive(
  route: RouteExtension,
  path: string
): boolean {
  // Check if this route matches
  if (route.path === path) {
    return true;
  }

  // Check if path starts with route path (for nested routes)
  if (path.startsWith(route.path + '/')) {
    return true;
  }

  // Check children
  if (route.children && route.children.length > 0) {
    return route.children.some((child) =>
      isRouteOrDescendantActive(child, path)
    );
  }

  return false;
}
